import { useMemo } from 'react';
import { useGetMyPermissionsQuery } from '../services/userApi';

/**
 * Returns helpers for permission-based UI gating.
 *
 * Usage:
 *   const { can, isLoading } = usePermission();
 *   if (can('ORDER_VIEW')) { ... }
 */
export function usePermission() {
  const {
    data: permissions = [],
    isLoading,
    isFetching,
  } = useGetMyPermissionsQuery();

  // Build fast lookup Sets for action IDs and resource IDs
  const allowedActionIds = useMemo(
    () => new Set(permissions.map(p => p.actionId)),
    [permissions]
  );
  const allowedResourceIds = useMemo(
    () => new Set(permissions.map(p => p.resourceId)),
    [permissions]
  );

  /** Check if the user can perform a specific action (by action ID). */
  const canByActionId = (actionId: string): boolean =>
    allowedActionIds.has(actionId);

  /** Check if the user can access a specific resource (by resource ID). */
  const canByResourceId = (resourceId: string): boolean =>
    allowedResourceIds.has(resourceId);

  return {
    permissions,
    canByActionId,
    canByResourceId,
    isLoading: isLoading || isFetching,
  };
}
